import { typographyVariant } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import * as React from 'react';

/**
 * Everything a `div` takes, minus the three the chip owns itself, PLUS the two
 * it declares. Extending the DOM props is what makes the chip usable as an
 * `asChild` trigger: a wrapper like Radix's `HoverCardTrigger` injects
 * `data-state` and five pointer/focus handlers into whatever it clones, and a
 * closed prop list drops them silently — see the docblock below.
 *
 * `onClick` is re-declared rather than inherited because the chip's lands on the
 * inner name element, not on the root, and it takes no event.
 */
interface LinkedPatientChipProps extends Omit<
  React.ComponentPropsWithoutRef<'div'>,
  'onClick' | 'children' | 'className'
> {
  /** Display name of the linked patient. */
  name: string;
  /**
   * Click handler — typically opens the patient details drawer. When
   * omitted (e.g. assistant role without edit rights) the name renders
   * as plain non-interactive text.
   */
  onClick?: () => void;
  'data-testid'?: string;
}

/**
 * The in-input UI shown when the session is already linked to a patient
 * profile. Replaces the search input + dropdown surface entirely until
 * the patient is unlinked.
 *
 * The recap hover card is NOT built in. It reads the current session id and
 * emits `patient_recap_popover_opened`, both of which are app-side, so the
 * surface that composes this chip wraps it in `LinkedPatientHoverCard`
 * instead — which is also how `V2PatientLinkTrigger` has always used it.
 *
 * THAT MAKES THE CHIP AN `asChild` TARGET, AND THE REST SPREAD IS LOAD-BEARING.
 * `HoverCardTrigger asChild` clones its child with `data-state` and composed
 * `onPointerEnter` / `onPointerLeave` / `onFocus` / `onBlur` / `onTouchStart`.
 * While the chip wrapped itself, the trigger cloned a plain `div` element and
 * those landed on the DOM for free. Cloning a COMPONENT instead delivers them as
 * props, so a chip that destructured only its own three would drop all six and
 * the card would never open — no error, no failing render, just a hover
 * affordance that does nothing and an impression event that never fires. The
 * spread goes on the ROOT, which is the node the trigger used to clone, and it
 * goes FIRST so the chip's own `data-testid` and `className` still win.
 */
export const LinkedPatientChip = React.forwardRef<HTMLDivElement, LinkedPatientChipProps>(
  ({ name, onClick, 'data-testid': testId, ...triggerProps }, ref) => {
    const NameTag = onClick ? 'button' : 'span';
    return (
      <div
        {...triggerProps}
        ref={ref}
        data-testid={testId}
        className="group flex items-center gap-x-1"
      >
        <NameTag
          type={onClick ? 'button' : undefined}
          onClick={onClick}
          className={cn(
            typographyVariant({ variant: 'h4' }),
            'truncate text-start',
            onClick &&
              'cursor-pointer rounded-sm bg-transparent p-0 outline-none hover:underline focus-visible:underline'
          )}
        >
          {name}
        </NameTag>
      </div>
    );
  }
);
LinkedPatientChip.displayName = 'LinkedPatientChip';
